import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import {
  Box,
  Badge,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { NotificationStyled } from '../../components/styled';
import { api, useHttpMutation } from '../../../../api';
import { useSelector } from 'react-redux';
import { Store } from '../../../../redux';
import { useMutation } from 'react-query';
import { API_URLS } from '../../consts';
import { SendMessage } from '../../use-websocket';
import { Popover, PopoverContent } from '@/shared/components/ui/popover';
import { PopoverTrigger } from '@radix-ui/react-popover';

export interface CardsRequestNotifierProps {
  requestUsers: string[];
  sendMessage: SendMessage;
}

export declare type CardsRequestInfo = {
  id: string;
  email: string;
  name: string;
  userType: string;
};

export const CardsRequestNotifier: FC<CardsRequestNotifierProps> = ({
  requestUsers,
  sendMessage,
}) => {
  const { mutate: accept } = useHttpMutation<unknown, { userID: string }>(
    API_URLS.ACCEPT_CARDS_REQUEST,
    'POST'
  );

  const token = useSelector((state: Store) => state.userReducer.token);
  const [info, setInfo] = useState<CardsRequestInfo[]>([]);
  const {
    mutateAsync: getUser,
    isLoading,
    isError,
  } = useMutation({
    mutationFn: async (id: string) => {
      const res = await api({
        method: 'GET',
        url: API_URLS.GET_USER_BY_ID.replace(':id', id),
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      return res.data;
    },
  });

  useEffect(() => {
    if (requestUsers.length) {
      const getInfo = async () => {
        const info = await Promise.all<CardsRequestInfo>(
          requestUsers.map(id => getUser(id))
        );

        setInfo(info);
      };

      getInfo();
    }
  }, [getUser, requestUsers]);

  const handleAccept = (userID: string, userType: string) => () => {
    accept(
      { userID },
      {
        onSuccess: () => {
          sendMessage('accepted-cards-request');
          if (userType === 'bot') {
            // In case of bot, we trigger message again so bot can throw card.
            setTimeout(() => {
              sendMessage('trigger bot to throw card');
            }, 800);
          }
        },
      }
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge badgeContent={requestUsers.length} color="error">
          <CircleNotificationsIcon fontSize="large" />
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="mx-2 w-80 p-1 shadow-lg bg-slate-300 dark:bg-slate-600 overflow-auto border-none"
      >
        <>
          {isLoading && !isError ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="100%"
            >
              <CircularProgress color="primary" size={50} />
            </Box>
          ) : (
            info.map((n, i) => (
              <NotificationStyled
                key={[n.id, i].join('-')}
                component={Paper}
                mt={1}
                gridTemplateColumns="1fr auto"
              >
                <Typography variant="body1">
                  <Typography component="span" color="primary">
                    {n.name}
                  </Typography>
                  {` is requesting for your cards. If you accept, you will
                  get rid of your cards and your cards will be given to`}{' '}
                  <Typography component="span" color="primary">
                    {n.name}.
                  </Typography>
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ height: 40 }}
                  onClick={handleAccept(n.id, n.userType)}
                >
                  Accept
                </Button>
              </NotificationStyled>
            ))
          )}
        </>
      </PopoverContent>
    </Popover>
  );
};
